/**
 * Renders fulfillment and payment status badges for paid orders (PBI 1011).
 * Payment badge is omitted when status is `None` to reduce noise on draft pre-orders.
 *
 * Also surfaces bounded placement retries (PBI #1220): an order whose vendor call failed
 * recoverably keeps placement-eligible axes while it is retried, which would otherwise be
 * indistinguishable from an order that has never been attempted.
 */

import { Badge, Group } from '@mantine/core';
import type { FulfillmentStatus, PaymentStatus } from '../../types';
import { PaymentStatus as PaymentStatusEnum } from '../../types';
import {
  FULFILLMENT_STATUS_COLORS,
  PAYMENT_STATUS_COLORS,
  RETRYING_PLACEMENT_BADGE_COLOR,
  formatRetryingBadgeLabel,
  formatStatusLabel,
} from '../../utils/orderStatusDisplay';
import type { PlacementRetryStatus } from '../../utils/orderStatusDisplay';

export interface OrderStatusBadgesProps {
  fulfillmentStatus: FulfillmentStatus;
  paymentStatus: PaymentStatus;
  size?: 'xs' | 'sm' | 'md' | 'lg';

  /**
   * Placement retry fields from the order DTO. When the order is still being retried a
   * `Retrying (attempt N)` badge is appended.
   */
  retryStatus?: Partial<PlacementRetryStatus> | null;
}

export function OrderStatusBadges({
  fulfillmentStatus,
  paymentStatus,
  size = 'sm',
  retryStatus = null,
}: OrderStatusBadgesProps) {
  const retryingLabel = formatRetryingBadgeLabel(retryStatus);

  return (
    <Group gap={4} wrap="wrap">
      <Badge
        color={FULFILLMENT_STATUS_COLORS[fulfillmentStatus]}
        size={size}
        variant="light"
      >
        {formatStatusLabel(fulfillmentStatus)}
      </Badge>
      {paymentStatus !== PaymentStatusEnum.None && (
        <Badge
          color={PAYMENT_STATUS_COLORS[paymentStatus]}
          size={size}
          variant="light"
        >
          {formatStatusLabel(paymentStatus)}
        </Badge>
      )}
      {retryingLabel && (
        <Badge color={RETRYING_PLACEMENT_BADGE_COLOR} size={size} variant="light">
          {retryingLabel}
        </Badge>
      )}
    </Group>
  );
}
